import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { MapPin, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { resolvePlaceToProperty } from '@/lib/callables'
import { openPropertyProfile } from '@/lib/property-navigation'
import { cn } from '@/lib/utils'

export type MapLocationTarget = {
  lat: number
  lng: number
  zoom?: number
  bounds?: { north: number; south: number; east: number; west: number }
}

type Suggestion = {
  placeId: string
  primary: string
  secondary: string
}

type PlacesLib = NonNullable<ReturnType<typeof useMapsLibrary<'places'>>>
type SessionToken = InstanceType<PlacesLib['AutocompleteSessionToken']>
type PlacePrediction = NonNullable<
  Parameters<
    NonNullable<Parameters<PlacesLib['AutocompleteService']['prototype']['getPlacePredictions']>[1]>
  >[0]
>[number]
type PlaceResult = NonNullable<
  Parameters<NonNullable<Parameters<PlacesLib['PlacesService']['prototype']['getDetails']>[1]>>[0]
>

/**
 * Map Places search: resolve to a Biddle property when possible (fly + pin + profile);
 * otherwise navigate the map like Google Places (suburb / landmark / unknown address).
 */
export function MapLocationSearch({
  onLocate,
  className,
}: {
  onLocate: (target: MapLocationTarget) => void
  className?: string
}) {
  const navigate = useNavigate()
  const places = useMapsLibrary('places')
  const listId = useId()
  const onLocateRef = useRef(onLocate)
  const sessionTokenRef = useRef<SessionToken | null>(null)
  const debounceRef = useRef<number | null>(null)
  /** When set, prediction fetch is suppressed until the user edits the query. */
  const selectedLabelRef = useRef<string | null>(null)
  const requestIdRef = useRef(0)

  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [resolving, setResolving] = useState(false)

  useEffect(() => {
    onLocateRef.current = onLocate
  }, [onLocate])

  useEffect(() => {
    if (!places) return
    sessionTokenRef.current = new places.AutocompleteSessionToken()
  }, [places])

  useEffect(() => {
    if (!places) return

    // Selection filled the input — do not reopen the dropdown for that label.
    if (selectedLabelRef.current != null && query === selectedLabelRef.current) {
      setSuggestions([])
      setOpen(false)
      setLoading(false)
      return
    }

    const q = query.trim()
    if (q.length < 2) {
      setSuggestions([])
      setOpen(false)
      setLoading(false)
      return
    }

    if (debounceRef.current != null) window.clearTimeout(debounceRef.current)
    const requestId = ++requestIdRef.current
    setLoading(true)
    debounceRef.current = window.setTimeout(() => {
      const service = new places.AutocompleteService()
      service.getPlacePredictions(
        {
          input: q,
          componentRestrictions: { country: 'au' },
          sessionToken: sessionTokenRef.current ?? undefined,
        },
        (predictions: PlacePrediction[] | null, status: string) => {
          if (requestId !== requestIdRef.current) return
          if (selectedLabelRef.current != null && query === selectedLabelRef.current) {
            setLoading(false)
            setSuggestions([])
            setOpen(false)
            return
          }
          setLoading(false)
          if (status !== places.PlacesServiceStatus.OK || !predictions) {
            setSuggestions([])
            setOpen(false)
            return
          }
          setSuggestions(
            predictions.map((p: PlacePrediction) => ({
              placeId: p.place_id,
              primary: p.structured_formatting?.main_text || p.description,
              secondary: p.structured_formatting?.secondary_text || '',
            })),
          )
          setOpen(true)
          setActiveIndex(-1)
        },
      )
    }, 200)

    return () => {
      if (debounceRef.current != null) window.clearTimeout(debounceRef.current)
    }
  }, [places, query])

  function flyToPlace(place: PlaceResult) {
    const location = place.geometry?.location
    if (!location) return

    const viewport = place.geometry?.viewport
    if (viewport) {
      const ne = viewport.getNorthEast()
      const sw = viewport.getSouthWest()
      onLocateRef.current({
        lat: location.lat(),
        lng: location.lng(),
        bounds: {
          north: ne.lat(),
          east: ne.lng(),
          south: sw.lat(),
          west: sw.lng(),
        },
      })
      return
    }

    onLocateRef.current({ lat: location.lat(), lng: location.lng(), zoom: 14 })
  }

  function resolvePlace(placeId: string) {
    if (!places || resolving) return

    if (debounceRef.current != null) {
      window.clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    // Invalidate any in-flight prediction responses.
    requestIdRef.current += 1

    setResolving(true)
    setOpen(false)
    setSuggestions([])
    setActiveIndex(-1)
    setLoading(false)

    const attribution = document.createElement('div')
    const service = new places.PlacesService(attribution)
    service.getDetails(
      {
        placeId,
        fields: ['geometry', 'name', 'formatted_address'],
        sessionToken: sessionTokenRef.current ?? undefined,
      },
      (place: PlaceResult | null, status: string) => {
        void (async () => {
          sessionTokenRef.current = new places.AutocompleteSessionToken()

          if (status !== places.PlacesServiceStatus.OK || !place?.geometry?.location) {
            setResolving(false)
            return
          }

          const label = place.formatted_address || place.name || query
          selectedLabelRef.current = label
          setQuery(label)
          setSuggestions([])
          setOpen(false)

          try {
            const result = await resolvePlaceToProperty(placeId, label)
            if (result.status === 'found' && result.property?.id) {
              setQuery('')
              selectedLabelRef.current = null
              // MapPage loads the pin + flies via ?property= focus effect.
              openPropertyProfile(navigate, result.property.id)
              return
            }
          } catch {
            // Fall through to Places navigation.
          } finally {
            setResolving(false)
          }

          flyToPlace(place)
        })()
      },
    )
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false)
      return
    }

    if (e.key === 'ArrowDown') {
      if (suggestions.length === 0) return
      e.preventDefault()
      setOpen(true)
      setActiveIndex((i) => Math.min(i < 0 ? 0 : i + 1, suggestions.length - 1))
      return
    }

    if (e.key === 'ArrowUp') {
      if (suggestions.length === 0) return
      e.preventDefault()
      setOpen(true)
      setActiveIndex((i) => Math.max(i < 0 ? suggestions.length - 1 : i - 1, 0))
      return
    }

    if (e.key === 'Enter') {
      if (suggestions.length === 0) return
      e.preventDefault()
      // Default to the top suggestion when none is highlighted.
      const index = activeIndex >= 0 ? activeIndex : 0
      resolvePlace(suggestions[index].placeId)
    }
  }

  return (
    <div className={cn('relative w-full', className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted" />
      <Input
        value={query}
        onChange={(e) => {
          selectedLabelRef.current = null
          setQuery(e.target.value)
        }}
        onFocus={() => {
          if (
            selectedLabelRef.current == null &&
            suggestions.length > 0 &&
            query.trim().length >= 2
          ) {
            setOpen(true)
          }
        }}
        onBlur={() => {
          window.setTimeout(() => setOpen(false), 150)
        }}
        onKeyDown={onKeyDown}
        disabled={!places || resolving}
        placeholder="Search an address, suburb, or place…"
        className="h-12 bg-white pl-10 pr-10 text-base shadow-lg focus-visible:ring-1 focus-visible:ring-biddle-blue"
        aria-label="Search for a property or location on the map"
        aria-autocomplete="list"
        aria-controls={listId}
        aria-expanded={open}
        autoComplete="off"
      />
      {loading || resolving ? (
        <Spinner className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2" />
      ) : null}

      {open && suggestions.length > 0 ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-30 mt-2 max-h-80 w-full overflow-auto rounded-2xl border border-border bg-white py-1 shadow-xl"
        >
          {suggestions.map((item, index) => (
            <li key={item.placeId} role="option" aria-selected={index === activeIndex}>
              <button
                type="button"
                className={cn(
                  'flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-surface',
                  index === activeIndex && 'bg-surface',
                )}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => resolvePlace(item.placeId)}
              >
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted" />
                <span>
                  <span className="block text-sm font-medium text-navy">{item.primary}</span>
                  {item.secondary ? (
                    <span className="block text-xs text-muted">{item.secondary}</span>
                  ) : null}
                </span>
              </button>
            </li>
          ))}
          <li className="px-4 py-2 text-right text-[10px] text-muted">powered by Google</li>
        </ul>
      ) : null}
    </div>
  )
}
